import React, { useState, useRef } from 'react';
import './TerminologyModal.css';
import { FaPlus, FaTrash, FaTimes, FaFileDownload, FaHistory } from 'react-icons/fa';
import { useClickOutside } from './hooks';
import { fetchHistory, fetchTerminologyDict } from './api';
import HistoryDropdown from './HistoryDropdown';

const TerminologyModal = ({ show, onClose, onSave, formId }) => {
  const [entries, setEntries] = useState([]);
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [historyTasks, setHistoryTasks] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const historyDropdownRef = useRef(null);
  const importButtonRef = useRef(null);

  useClickOutside(historyDropdownRef, importButtonRef, () => setShowHistory(false));

  const addEntry = () => {
    if (key && value) {
      const newEntries = [...entries, { key, value }];
      setEntries(newEntries);
      setKey('');
      setValue('');
    }
  };

  const removeEntry = (index) => {
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
  };

  const clearAll = () => {
    setEntries([]);
  };

  const importFromHistory = async () => {
    setLoading(true);
    try {
      const tasks = await fetchHistory();
      setHistoryTasks(tasks);
      setShowHistory(true);
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleHistorySelection = async (uuid) => {
    setLoading(true);
    try {
      const newEntries = await fetchTerminologyDict(uuid);
      setEntries(newEntries);
      setShowHistory(false);
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (key && value) {
      onSave([...entries, { key, value }]);
    } else {
      onSave(entries);
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>Create Project Dictionary</h2>
          <button onClick={onClose} className="close-button"><FaTimes /></button>
        </div>
        <p className="modal-description">By telling us more specific translation mappings, we can help you check more.
        </p>
        <p className="modal-description">For example: <u><i>"technician"</i></u> is usually translated as "技师、技术人员", but it is translated as <u><i></i>"工程师"</u> in this project.
        </p>
        <div className="modal-content">
          <div className="entry-form">
            <input
              type="text"
              placeholder="English, e.g.: technician centers"
              value={key}
              onChange={(e) => setKey(e.target.value)}
            />
            <input
              type="text"
              placeholder="Chinese, e.g.: 工程师驿站"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <button onClick={addEntry}><FaPlus /> Add</button>
          </div>
          <div className="entry-list">
            {entries.map((entry, index) => (
              <div key={index} className="entry-item">
                <span title={entry.key}>{entry.key}</span>
                <span title={entry.value}>{entry.value}</span>
                <button className="close-button" onClick={() => removeEntry(index)}><FaTimes /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <div className="footer-button-group">
            <button ref={importButtonRef} onClick={importFromHistory} className="import-history-button" disabled={loading}>
              {loading ? 'Loading...' : <><FaHistory /> Import From History</>}
            </button>
            {showHistory && (
              <HistoryDropdown
                tasks={historyTasks}
                onSelect={handleHistorySelection}
                dropdownRef={historyDropdownRef}
              />
            )}
          </div>
          <div className="footer-button-group">
            <button onClick={handleConfirm}><FaFileDownload /> Confirm</button>
            <button onClick={clearAll} className="clear-all-button"><FaTrash /> Clear All</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TerminologyModal;